import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import CartesiaCall from "../components/CartesiaCall";
import VoiceGenderSelector from "../components/VoiceGenderSelector";

/**
 * Demo PRO: pagina dedicata per un cliente specifico (es. Tenuta Ciminata Greco).
 * Bypassa il form sito+email, usa una knowledge base hardcoded server-side
 * caricata via /api/demo-pro/{slug}.
 */
export default function DemoPro() {
  const { slug } = useParams();
  const [demoMeta, setDemoMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [callOpen, setCallOpen] = useState(false);
  const [selectedGender, setSelectedGender] = useState(null);
  const [micStatus, setMicStatus] = useState("idle"); // idle | requesting | denied
  const callIdRef = useRef(null); // ID univoco per sessione di chiamata

  const getCallId = () => {
    if (!callIdRef.current) {
      callIdRef.current = `${slug}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    }
    return callIdRef.current;
  };

  const saveTranscriptLine = useCallback((role, content) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    const call_id = getCallId();
    fetch(`${backend}/api/demo-pro/${slug}/transcript`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ role, content, call_id }),
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const backend = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${backend}/api/demo-pro/${slug}`);
        if (!r.ok) throw new Error("Demo non trovata");
        const data = await r.json();
        if (!cancelled) {
          setDemoMeta(data);
          setLoading(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Errore caricamento demo");
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const handleGenderSelect = async (gender) => {
    setMicStatus("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
      setMicStatus("idle");
      setSelectedGender(gender);
      setCallOpen(true);
    } catch (e) {
      setMicStatus("denied");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <Loader2 className="w-8 h-8 animate-spin text-brand-terra" />
      </div>
    );
  }

  if (error || !demoMeta) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-brand-bg p-6">
        <AlertTriangle className="w-10 h-10 text-brand-terra mb-4" />
        <h1 className="font-heading text-2xl text-brand-ink mb-2">Demo non disponibile</h1>
        <p className="text-brand-inkSoft text-center max-w-md mb-6">
          {error || "Impossibile caricare la demo richiesta."}
        </p>
        <Link
          to="/"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-ink text-brand-bg text-sm font-medium"
          data-testid="demopro-back-home"
        >
          <ArrowLeft className="w-4 h-4" /> Torna alla home
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg" data-testid="demo-pro-page">
      {/* Header minimal */}
      <header className="border-b border-brand-line py-5 px-6 flex items-center justify-end">
        <span className="text-xs font-medium tracking-[0.2em] text-brand-terra uppercase">
          Demo dedicata
        </span>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-xl text-center">
          {/* Nome azienda */}
          <h1
            className="font-heading text-3xl sm:text-4xl text-brand-ink mb-3 leading-tight"
            data-testid="demopro-company-name"
          >
            {demoMeta.company_name}
          </h1>
          {demoMeta.tagline && (
            <p
              className="text-base text-brand-inkSoft mb-10 italic"
              data-testid="demopro-tagline"
            >
              {demoMeta.tagline}
            </p>
          )}

          {/* Intro alla demo */}
          <p
            className="text-sm sm:text-base text-brand-inkSoft mb-10 leading-relaxed max-w-md mx-auto"
            data-testid="demopro-intro"
          >
            Questa è la sua segretaria 24Vox personalizzata. Conosce la tenuta, la storia,
            i servizi e sa come rispondere ai clienti al telefono.
            Scelga prima quale voce userà.
          </p>

          {/* Selettore voce */}
          <VoiceGenderSelector onSelect={handleGenderSelect} />
        </div>
      </main>

      <footer className="text-center text-xs text-brand-inkSoft py-6 border-t border-brand-line">
        Demo realizzata da 24Vox · voce reale, conoscenza dedicata
      </footer>

      {/* Modal chiamata */}
      {micStatus === "requesting" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/80 backdrop-blur-md p-4">
          <div className="bg-brand-bg rounded-3xl shadow-2xl border border-brand-line p-8 text-center max-w-sm w-full">
            <Loader2 className="w-10 h-10 text-brand-terra animate-spin mx-auto mb-4" />
            <h2 className="font-heading text-xl text-brand-ink mb-2">Accesso al microfono</h2>
            <p className="text-sm text-brand-inkSoft">
              Il browser sta chiedendo il permesso per il microfono.<br />
              <strong>Clicca "Consenti"</strong> nella barra in alto per avviare la demo.
            </p>
          </div>
        </div>
      )}

      {micStatus === "denied" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/80 backdrop-blur-md p-4">
          <div className="bg-brand-bg rounded-3xl shadow-2xl border border-brand-line p-8 text-center max-w-sm w-full">
            <AlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl text-brand-ink mb-2">Microfono bloccato</h2>
            <p className="text-sm text-brand-inkSoft mb-5">
              Per usare la demo vocale hai bisogno del microfono.<br />
              Clicca sull'icona del lucchetto nella barra dell'indirizzo e abilita il microfono, poi ricarica la pagina.
            </p>
            <button
              onClick={() => setMicStatus("idle")}
              className="px-5 py-2.5 rounded-full bg-brand-ink text-brand-bg text-sm font-medium"
            >
              Riprova
            </button>
          </div>
        </div>
      )}

      {callOpen && (
        <CartesiaCall
          sessionId={slug}
          companyName={demoMeta.company_name}
          gender={selectedGender}
          endpoint={`${process.env.REACT_APP_BACKEND_URL}/api/demo-pro/${slug}/cartesia/session`}
          endCallEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/demo-pro/${slug}/end-call?call_id=${encodeURIComponent(getCallId())}`}
          waPreviewEndpoint={`${process.env.REACT_APP_BACKEND_URL}/api/demo-pro/${slug}/wa-preview`}
          onClose={() => {
            callIdRef.current = null;
            setCallOpen(false);
            toast.success("Chiamata terminata. Riassunto in arrivo via email.");
          }}
          onTranscriptUpdate={(entry) => {
            if (entry && entry.role && entry.content) {
              saveTranscriptLine(entry.role, entry.content);
            }
          }}
        />
      )}
    </div>
  );
}
